//@ts-ignore
import axios, { InternalAxiosRequestConfig } from "axios";
import { getAccessToken } from "./app.data";

type RequestConfigWithMeta = InternalAxiosRequestConfig & {
	meta?: {
		requestStartedAt?: number;
	};
};

export const axiosInstance = axios.create({
	//baseURL: process.env.HOST_API || "https://trace.lucidarray.dev/",
	timeout: 120000,
	headers: {
		"Content-Type": "application/json",
	},
});

axiosInstance.interceptors.request.use(
	(config: InternalAxiosRequestConfig) => {
		const token = getAccessToken();
		if (token) config.headers.Authorization = `Bearer ${token}`;
		return config;
	},
	(error: any) => {
		return Promise.reject(error);
	},
);

axiosInstance.interceptors.request.use((req: RequestConfigWithMeta) => {
	req.meta = req.meta || {};
	req.meta.requestStartedAt = Date.now();
	return req;
});

axiosInstance.interceptors.response.use(
	(res) => {
		const requestStartedAt = (res.config as RequestConfigWithMeta).meta
			?.requestStartedAt;
		console.log(
			`Execution time for: ${res.config.url}[${res.config.method}] - ${millisToMinutesAndSeconds(Date.now() - (requestStartedAt ?? Date.now()))}`,
		);
		return res;
	},
	(res) => {
		const requestStartedAt = (res.config as RequestConfigWithMeta).meta
			?.requestStartedAt;
		console.warn(
			`Execution time for: ${res.config.url}[${res.config.method}] - ${millisToMinutesAndSeconds(Date.now() - (requestStartedAt ?? Date.now()))}`,
		);
		return Promise.reject(res);
	},
);

export class HttpClient {
	static async get<T>(url: string, params?: any) {
		const response = await axiosInstance.get<T>(url, { params });
		return response.data;
	}

	static async post<T>(
		url: string,
		data?: any,
		options?: any,
		doNotFilter?: "no-filter",
	) {
		if (!doNotFilter) {
			filterPayloadData(data);
		}

		const response = await axiosInstance.post<T>(url, data, options);
		return response.data;
	}

	static async postFile<T>(url: string, data: any, options?: any) {
		const response = await axiosInstance.post<T>(url, data, {
			headers: {
				"Content-Type": "multipart/form-data",
				...options,
			},
		});
		return response.data;
	}

	static async put<T>(url: string, data: any, doNotFilter?: "no-filter") {
		if (!doNotFilter) {
			filterPayloadData(data);
		}

		const response = await axiosInstance.put<T>(url, data);
		return response.data;
	}

	static async patch<T>(url: string, data: any, doNotFilter?: "no-filter") {
		if (!doNotFilter) {
			filterPayloadData(data);
		}

		const response = await axiosInstance.patch<T>(url, data);
		return response.data;
	}

	static async delete<T>(url: string, params?: any) {
		const response = await axiosInstance.delete<T>(url, { params });
		return response.data;
	}
}

/**
 * Converts milliseconds to a formatted string of minutes and seconds.
 * @param millis - The time in milliseconds to convert
 * @returns A string in the format "MM:SS" where MM is minutes and SS is seconds
 * @example
 * // Returns "1:30"
 * millisToMinutesAndSeconds(90000)
 */
function millisToMinutesAndSeconds(millis: number) {
	const date = new Date(millis);
	return `${date.getMinutes()}:${date.getSeconds()}`;
}

/**
 * Filters and cleans up payload data by removing properties with null/undefined values and trimming string values.
 * This utility function modifies the input object directly.
 *
 * @param data - The object to be filtered
 *
 * @example
 * const payload = {
 *   name: "  John Doe  ",
 *   age: null,
 *   active: false,
 *   count: 0
 * };
 * filterPayloadData(payload);
 * // payload becomes { name: "John Doe", active: false, count: 0 }
 */
export function filterPayloadData(data: any) {
	if (data) {
		Object.entries(data)?.forEach(([key, value]) => {
			if (typeof value === "string") {
				data[key] = value.trim();
			}
			if (value !== 0 && value !== false && !value) {
				delete data[key];
			}
		});
	}
}
